/**
 * TimelineEvent — the height-aware event block used on the full timeline.
 *
 * Unlike the list `EventCard`, a timeline block has a fixed pixel height (its
 * duration), so it can only show the fields that fit. It reveals title →
 * location → owners as height allows, vertically centers when only the
 * time row fits, and clips nothing mid-line. In narrow overlap columns the
 * status collapses to a dot (the label is dropped) to avoid colliding with
 * the time.
 */

import { MapPin } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CSSProperties } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { OwnerAvatars } from './OwnerAvatars';
import { EventDetail } from './EventDetail';
import { formatTimeRange, statusAccent } from './calendar.helpers';
import type { NormalizedEvent } from './calendar.types';
import { cleanDisplayText } from '@/utils/display-text';

// Pixel-height thresholds at which each field becomes visible (ROW_HEIGHT=60,
// so 30m≈30px, 1h≈60px, 1.5h≈90px).
const SHOW_TITLE = 36;
const SHOW_LOCATION = 58;
const SHOW_OWNERS = 82;
const SHOW_STATUS_LABEL = 48;

export interface TimelineEventProps {
  event: NormalizedEvent;
  heightPx: number;
  /** In a multi-column overlap cluster → drop the status label, tighten. */
  narrow?: boolean;
  hour12?: boolean;
}

export function TimelineEvent({ event, heightPx, narrow = false, hour12 }: TimelineEventProps) {
  const accent = statusAccent(event.status);
  const showTitle = heightPx >= SHOW_TITLE;
  const showLocation = heightPx >= SHOW_LOCATION && !!event.location;
  const showOwners = heightPx >= SHOW_OWNERS && !!event.owners?.length;
  const showStatusLabel = !narrow && heightPx >= SHOW_STATUS_LABEL && !!accent;
  const onlyTime = !showTitle && !showLocation && !showOwners;

  const style: CSSProperties | undefined = event.color
    ? { borderLeftColor: event.color }
    : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="calendar-timeline-event"
          style={style}
          className={cn(
            'flex h-full w-full flex-col overflow-hidden rounded-md border border-border border-l-2 bg-card text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/50 focus-visible:ring-offset-[3px] focus-visible:ring-offset-background',
            accent?.border,
            narrow ? 'px-1.5' : 'px-2',
            onlyTime ? 'justify-center py-0' : 'gap-0.5 py-1',
          )}
        >
          {/* Time + status (single line). When too short to stack the title on
              its own line, the title rides inline next to the time. */}
      <div className="flex items-center justify-between gap-1.5">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] leading-tight">
          <span className="shrink-0 text-muted-foreground tabular-nums">
            {formatTimeRange(event, hour12)}
          </span>
          {!showTitle && (
            <span className="truncate font-medium text-foreground">{cleanDisplayText(event.title)}</span>
          )}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {event.unread && <span aria-label="Unread" className="size-1.5 rounded-full bg-status-info-fg" />}
          {accent && (
            <>
              <span className={cn('size-1.5 rounded-full', accent.dot)} aria-hidden />
              {showStatusLabel && (
                <span className={cn('text-[11px] font-medium leading-tight', accent.text)}>
                  {accent.label}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {showTitle && (
        <h4 className="truncate text-xs font-medium leading-tight text-foreground">{cleanDisplayText(event.title)}</h4>
      )}

      {showLocation && (
        <div className="flex min-w-0 items-center gap-1 text-[11px] leading-tight text-muted-foreground">
          <MapPin className="size-3 shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>
      )}

          {showOwners && (
            <div className="mt-0.5">
              <OwnerAvatars owners={event.owners!} size={20} max={4} showName={false} />
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        // Cap to the space Radix actually has on the chosen side (not a flat
        // 80vh, which can still overflow when the popover opens partway down
        // the viewport) and scroll the overflow.
        className="w-[26rem] max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
      >
        <EventDetail event={event} hour12={hour12} />
      </PopoverContent>
    </Popover>
  );
}
